import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, RefreshCw, Loader2 } from "lucide-react";
import { SlabShell, CardBackFace } from "@/components/CardParts";

export const zoomCard = (url, name, slab, game) =>
  window.dispatchEvent(new CustomEvent("card-zoom", { detail: { url, name, slab, game } }));

const STAT_KEYS = ["dink", "power", "speed", "iq", "clutch"];
const ABILITY_META = {
  BOOST: { cls: "border-sky-400/60 text-sky-300", fallback: "+6 to this card's round stat" },
  TRIGGER: { cls: "border-fuchsia-400/60 text-fuchsia-300", fallback: "+10 comeback surge when you're trailing as this card plays" },
  CLASH: { cls: "border-pb-red/70 text-pb-red", fallback: "-4 to every opponent's score in this card's round" },
  CLUTCH: { cls: "border-yellow-400/60 text-yellow-300", fallback: "Doubles the round-5 CLUTCH bonus (+3 in earlier rounds)" },
};
const PROTECTION_EXPLAIN = {
  "RAW": "Unprotected — no bonus, and raw cards can lose grade as they age",
  "SLEEVED": "Penny Sleeve — aging slowed by half",
  "TOP LOADED": "Top Loader — aging stopped, grade locked forever",
  "GRADED": "Sealed slab (unsigned legacy — frozen at this tier)",
  "GRADED + SIGNED": "Signed, then slabbed",
  "NUMBERED SLAB": "A signed slab from a hand-numbered run",
  "BLACK LABEL": "PERFECT Black Label 10 slab + signed",
  "BLACK LABEL №": "The apex — numbered, signed, PERFECT Black Label 10",
};

const BattleSheet = ({ g }) => {
  const ab = g.ability;
  const meta = ab ? (ABILITY_META[ab.type] || {}) : null;
  return (
    <div data-testid="card-zoom-battle-sheet" onClick={(e) => e.stopPropagation()}
      className="w-full sm:w-72 shrink-0 border border-yellow-400/40 bg-black/85 p-4 text-left cursor-default animate-fade-up">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[9px] font-black uppercase tracking-[0.25em] text-yellow-400">⚔ Battle Sheet</span>
        <span className="font-display font-black text-3xl text-yellow-400">{g.stats?.overall}<span className="text-[10px] ml-1 text-zinc-500">OVR</span></span>
      </div>
      <div className="space-y-1.5 mb-4">
        {STAT_KEYS.map((s) => (
          <div key={s} className="flex items-center gap-2" data-testid={`battle-sheet-stat-${s}`}>
            <span className={`w-14 text-[8px] font-black uppercase tracking-[0.2em] ${s === "clutch" ? "text-yellow-400" : "text-zinc-400"}`}>{s}</span>
            <div className="flex-1 h-1.5 bg-zinc-800">
              <div className={`h-full ${s === "clutch" ? "bg-yellow-400" : "bg-zinc-400"}`} style={{ width: `${g.stats?.[s] || 0}%` }} />
            </div>
            <span className="w-7 text-right text-[11px] font-mono font-bold">{g.stats?.[s]}</span>
          </div>
        ))}
        <p className="text-[8px] text-zinc-600 pt-0.5">Round 5 adds half this card's CLUTCH as a bonus.</p>
      </div>
      <div className="mb-3" data-testid="battle-sheet-ability">
        <div className="text-[8px] font-black uppercase tracking-[0.25em] text-zinc-500 mb-1">Special Ability</div>
        {ab ? (
          <div className={`border px-2.5 py-2 ${meta.cls}`}>
            <div className="text-[10px] font-black uppercase tracking-widest">⚡ {ab.type}</div>
            <div className="text-[10px] text-zinc-300 mt-0.5">{ab.desc || meta.fallback}</div>
          </div>
        ) : (
          <div className="border border-zinc-800 px-2.5 py-2 text-[10px] text-zinc-500">None — this card plays on pure stats</div>
        )}
      </div>
      <div data-testid="battle-sheet-protection">
        <div className="text-[8px] font-black uppercase tracking-[0.25em] text-zinc-500 mb-1">Protection Power</div>
        <div className={`border px-2.5 py-2 ${g.black_label ? "border-yellow-300/70 text-yellow-300" : g.protection_bonus > 0 ? "border-teal-400/50 text-teal-300" : "border-zinc-800 text-zinc-400"}`}>
          <div className="text-[10px] font-black uppercase tracking-widest">
            {g.black_label ? "★ " : "🛡 "}{g.protection_label}{g.protection_bonus > 0 ? ` · +${g.protection_bonus} to every stat` : ""}
          </div>
          <div className="text-[10px] text-zinc-400 mt-0.5">{PROTECTION_EXPLAIN[g.protection_label] || ""}</div>
        </div>
      </div>
    </div>
  );
};

export const CardZoomViewer = () => {
  const [card, setCard] = useState(null);
  const [showBack, setShowBack] = useState(false);
  const [back, setBack] = useState(null);
  const [backLoading, setBackLoading] = useState(false);
  useEffect(() => {
    const open = (e) => { setCard(e.detail); setShowBack(false); setBack(null); };
    const key = (e) => e.key === "Escape" && setCard(null);
    window.addEventListener("card-zoom", open);
    window.addEventListener("keydown", key);
    return () => { window.removeEventListener("card-zoom", open); window.removeEventListener("keydown", key); };
  }, []);
  if (!card) return null;
  const s = card.slab;
  const flip = async (e) => {
    e.stopPropagation();
    if (!showBack && !back && s?.card_id) {
      setBackLoading(true);
      try {
        const { data } = await api.get(`/cards/${s.card_id}/back`);
        setBack(data);
      } catch { setBackLoading(false); return; }
      setBackLoading(false);
    }
    setShowBack(!showBack);
  };
  return (
    <div data-testid="card-zoom-overlay" onClick={() => setCard(null)}
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-sm flex flex-col items-center justify-center p-4 cursor-zoom-out animate-fade-up overflow-y-auto">
      <button data-testid="card-zoom-close" onClick={() => setCard(null)} aria-label="Close"
        className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center border border-zinc-600 text-zinc-300 hover:border-pb-red hover:text-pb-red transition-colors bg-black/60 z-10">
        <X className="w-5 h-5" />
      </button>
      <div className="flex flex-col sm:flex-row items-center sm:items-start justify-center gap-4 max-w-full">
      {s ? (
        <div data-testid="card-zoom-slab" onClick={(e) => e.stopPropagation()} className="w-[min(46vh,84vw)] cursor-default">
          <div className="flip-card">
            <div className={`flip-inner front-sized ${showBack ? "is-flipped" : ""}`}>
              <div className="flip-front">
                <SlabShell c={s} testId="card-zoom" lg>
                  <img src={card.url} alt={card.name || "Card"} data-testid="card-zoom-image"
                    className="w-full aspect-[3/4] object-contain bg-black" />
                </SlabShell>
              </div>
              <div className="flip-back">
                <SlabShell c={s} testId="card-zoom-b" lg>
                  <CardBackFace back={back} copy={s} testId="card-zoom" framed lg />
                </SlabShell>
              </div>
            </div>
          </div>
          {s.card_id && (
            <button onClick={flip} data-testid="card-zoom-flip-btn" title="Flip the card"
              className="mt-3 mx-auto flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.25em] text-zinc-300 border border-zinc-600 px-4 py-2 bg-black/60 hover:border-pb-red hover:text-pb-red transition-colors">
              {backLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Flip Card
            </button>
          )}
        </div>
      ) : (
        <img src={card.url} alt={card.name || "Card"} data-testid="card-zoom-image"
          className="max-h-[80vh] max-w-[94vw] sm:max-w-[60vw] object-contain shadow-2xl" onClick={(e) => e.stopPropagation()} />
      )}
      {card.game?.stats && <BattleSheet g={card.game} />}
      </div>
      {card.name && !s && (
        <div data-testid="card-zoom-name" className="mt-3 text-xs font-black uppercase tracking-[0.25em] text-zinc-300">{card.name}</div>
      )}
    </div>
  );
};
